import { Assert } from "@seriously-rad/seriouslyrad-framework-core/Assert";
import { Nullable, PartialNullable } from "@seriously-rad/seriouslyrad-framework-core/Extensions";
import { ArrayHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/ArrayHelper";
import { ObjectHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/ObjectHelper";
import { StringHelper } from "@seriously-rad/seriouslyrad-framework-core/Helpers/StringHelper";
import { $$, IJNativeEvent, JNative, JNativeStatic } from "@seriously-rad/seriouslyrad-framework-core/JNative";
import { PropertyChange } from "@seriously-rad/seriouslyrad-framework-core/PropertyChange";
import { Property, PropertyBoolean, PropertyInt32 } from "@seriously-rad/seriouslyrad-framework-core/RadObject";
import { Control, ControlPropertyValues, IControl } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Control";
import { Point } from "@seriously-rad/seriouslyrad-framework-core/Ui/Point";
import { IRectangle } from "@seriously-rad/seriouslyrad-framework-core/Ui/Rectangle";
import { IResizableControl } from "./ResizableControl";
import { NAMESPACE } from "./_Namespace";

import "./ResizeBand.scss";

/*
                classCreated: (namespace, classConstructor) =>
                {
                    $$(document).on("click", ".rad-control-resize-band .rad-handle", evt =>
                    {
                        evt.preventDefault();
                        return false;
                    });
                }
*/

export enum ResizeBandResizeModeEnum
{
    all = "all",
    horizontal = "horizontal",
    vertical = "vertical",
}

export interface IResizeBandMoveStartArgs
{
    ctrlKey: boolean;
    currentPoint: Point;
    data?: any;
    manualStart?: boolean;
    newControl?: boolean;
    shiftKey: boolean;
    startPoint: Point;
}

export interface IResizeBandMoveInfo
{
    $$selectionRoot: JNative<HTMLElement>;
    cancel?: boolean;
    ctrlKey?: boolean;
    currentPoint: Point;
    currentPositionInfo: { left: number, top: number };
    data?: any;
    distinctParentNodes?: HTMLElement[];
    hasMoved?: boolean;
    hasScrolled?: boolean;
    manualStart?: boolean;
    newControl?: boolean;
    newPositions: {
        position: { left: number, top: number };
        selectionItem: ISelection;
        size: Nullable<{ height?: Nullable<number>, width?: Nullable<number> }>;
    }[];
    originalPositionInfo: { left: number, top: number };
    originalScrollInfo: { left: number, top: number };
    originalSelectionRootOffset: { left: number, top: number };
    positionInfo?: IPositionInfo[],
    selectionRootHeight?: number;
    selection?: ISelection[];
    shiftKey: boolean;
    startPoint: Point;
    valid?: boolean;
}

export interface IResizeBandResizeInfo
{
    $$handle: JNative<HTMLElement>;
    $$selectionRoot: JNative<HTMLElement>;
    cancel?: boolean;
    currentPoint: Point;
    currentPositionInfo: { left: number, top: number };
    currentSizeInfo: { height: number, width: number };
    hasScrolled?: boolean;
    originalPositionInfo: { left: number, top: number };
    originalScrollInfo: { left: number, top: number };
    originalSizeInfo: { height: number, width: number };
    startPoint: Point;
    valid?: boolean;
}

interface IDragDropArea
{
    $$element: JNative<HTMLElement>;
    depth: number;
    offset: { left: number, top: number };
}

interface IPositionInfo
{
    changedParent: boolean;
    dragDropArea: Nullable<IDragDropArea>;
    position: {
        left: number;
        top: number;
    };
    selectionItem: ISelection;
}

interface ISelection
{
    $$originalParentControl: JNative<HTMLElement>;
    associatedControl: IResizableControl;
    control: ResizeBand;
    originalPosition: { left: number, top: number };
    originalParentControl: IControl;
}

export type ResizeBandPropertyValues =
    ControlPropertyValues
    & {
        associatedControl: IResizableControl;
        canMove: boolean;
        canResize: boolean;
        gridSpacing: number;
        resizeMode: ResizeBandResizeModeEnum;
    };

export type ResizeBandProperties = {
    [name in keyof ResizeBandPropertyValues]: Property<ResizeBandPropertyValues[name]>
};

export interface IResizeBand extends IControl, ResizeBandProperties
{
}

export class ResizeBand extends Control implements IResizeBand
{
    // #region Static

    public static override classNamespace = NAMESPACE + "ResizeBand";
    public static override className = "ResizeBand";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ResizeBandPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                canMove: true,
                canResize: true,
                gridSpacing: 1,
                resizeMode: ResizeBandResizeModeEnum.all,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected moveInfo?: IResizeBandMoveInfo;
    public resizeInfo?: IResizeBandResizeInfo;

    // #endregion

    // #region Properties

    public associatedControl!: Property<IResizableControl>;
    public canMove!: PropertyBoolean;
    public canResize!: PropertyBoolean;
    public gridSpacing!: PropertyInt32;
    public resizeMode!: Property<ResizeBandResizeModeEnum>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ResizeBandPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-resize-band");

        this.visible.set(false);

        // Trigger Property Changes
        this.canMove.triggerChange();
        this.canResize.triggerChange();
        this.resizeMode.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public cancelMove()
    {
        if (!this.moveInfo)
            return;

        delete this.moveInfo;

        this._removeStateCss("moving");
    }
    public startMove(args: IResizeBandMoveStartArgs)
    {
        args.manualStart = true;
        this._moveStart(args);
    }

    // #endregion

    // #region Protected Methods

    protected _addStateCss(state: string)
    {
        this._get$$SelectionRoot().addClass(StringHelper.format("rad-state-{0}", state));
    }
    protected _associatedControlChanged(change: PropertyChange<IResizableControl>)
    {
        /* Intentionally empty */
    }
    protected _canMoveChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-state-can-move");
        else
            this.removeCssClass("rad-state-can-move");
    }
    protected _canResizeChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-state-can-resize");
        else
            this.removeCssClass("rad-state-can-resize");
    }
    public _clearSelection(except?: ResizeBand)
    {
        this._getSelection()
            .forEach(item =>
            {
                if (!except || item.control !== except)
                    item.control.hide();
            });
    }
    protected _createMoveInfo(baseMoveInfo: IResizeBandMoveInfo)
    {
        baseMoveInfo.$$selectionRoot = this._get$$SelectionRoot();
        baseMoveInfo.selectionRootHeight = baseMoveInfo.$$selectionRoot.outerHeight();
        baseMoveInfo.selection = this._getSelection();
        baseMoveInfo.distinctParentNodes = this._getDistinctParentNodes(baseMoveInfo.selection);
        return baseMoveInfo;
    }
    protected _get$$SelectionRoot(): JNative<HTMLElement>
    {
        const
            $$associatedControlElement = this.associatedControl.get()?.$$element.get();

        let
            $$selectionRoot = $$associatedControlElement?.closest(".rad-selection-root");

        if (!$$selectionRoot || !$$selectionRoot.length)
            $$selectionRoot = $$associatedControlElement?.parent();

        Assert.isNotNullOrUndefined($$selectionRoot, "$$selectionRoot");

        return $$selectionRoot as JNative<HTMLElement>;
    }
    protected _getAllAssociatedControls(): IResizableControl[]
    {
        return (
            this._get$$SelectionRoot()
                .find(".rad-control-resize-band")
                .toArray()
                .map(item => (JNativeStatic.radControl(item) as ResizeBand).associatedControl.get())
                .filter((item): item is IResizableControl => ObjectHelper.classInheritsClassName<IResizableControl>(item, "@seriously-rad/seriouslyrad-framework-module-reports/Ui/Controls/PrintReportDesigner/Controls/ResizableControl.ResizableControl"))
        );
    }
    protected _getControlOffsetRect(control: Control, padding: number)
    {
        const
            $$element = control.$$element.get();

        Assert.isNotNullOrUndefined($$element, "$$element");

        const
            itemOffset = $$element.offset(),
            itemWidth = $$element.outerWidth(),
            itemHeight = $$element.outerHeight();

        return {
            control: control,
            x1: (itemOffset?.left ?? 0) - padding,
            x2: (itemOffset?.left ?? 0) + itemWidth + padding,
            y1: (itemOffset?.top ?? 0) - padding,
            y2: (itemOffset?.top ?? 0) + itemHeight + padding
        };
    }
    protected _getDistinctParentNodes(selection: ISelection[]): HTMLElement[]
    {
        const
            parentNodes: HTMLElement[] = [];

        selection.forEach(item =>
        {
            const
                parentControlElement = item.control.parentControlGet()?.$$element.get()?.get(0);

            if (!parentControlElement)
                return;

            parentNodes.push(parentControlElement);
        });

        return ArrayHelper.distinct(parentNodes);
    }
    protected _getSelection(): ISelection[]
    {
        return (
            this._get$$SelectionRoot()
                .find(".rad-control-resize-band")
                .not(".rad-state-hidden")
                .toArray()
                .map(item =>
                {
                    const
                        $$element = $$(item),
                        control = $$element.radControl() as ResizeBand,
                        associatedControl = control.associatedControl.get(),
                        parentControl = associatedControl?.parentControlGet(),
                        $$parentControl = parentControl?.$$element.get(),
                        positionElement = $$element.position(),
                        position = {
                            left: positionElement.left ? Math.round(positionElement.left) : undefined,
                            top: positionElement.top ? Math.round(positionElement.top) : undefined,
                        };

                    Assert.isNotNullOrUndefined($$parentControl, "$$parentControl");
                    Assert.isNotNullOrUndefined(associatedControl, "associatedControl");
                    Assert.isNotNullOrUndefined(control, "control");
                    Assert.isNotNullOrUndefined(parentControl, "parentControl");

                    return {
                        $$originalParentControl: $$parentControl,
                        associatedControl: associatedControl,
                        control: control,
                        originalPosition: { left: position.left ?? 0, top: position.top ?? 0 },
                        originalParentControl: parentControl
                    };
                })
        );
    }
    protected _gridSpacingChanged(change: PropertyChange<number>)
    {
        /* Intentionally empty */
    }
    protected _moveChanged(cursorPoint: Nullable<Point>, shiftKey: boolean, ctrlKey: boolean)
    {
        if (!this.moveInfo)
            return;

        if (!cursorPoint)
            cursorPoint = this.moveInfo.currentPoint;

        this.moveInfo.shiftKey = shiftKey;
        this.moveInfo.ctrlKey = ctrlKey;

        const
            newScrollInfo = {
                left: this.moveInfo.$$selectionRoot.scrollLeft() ?? 0,
                top: this.moveInfo.$$selectionRoot.scrollTop() ?? 0,
            },
            newPositionInfo = {
                // Snap to grid
                left: this._roundValueToGrid(this.moveInfo.originalPositionInfo.left + cursorPoint.x - this.moveInfo.startPoint.x + newScrollInfo.left - this.moveInfo.originalScrollInfo.left),
                top: this._roundValueToGrid(this.moveInfo.originalPositionInfo.top + cursorPoint.y - this.moveInfo.startPoint.y + newScrollInfo.top - this.moveInfo.originalScrollInfo.top)
            },
            delta = {
                left: newPositionInfo.left - this.moveInfo.originalPositionInfo.left,
                top: newPositionInfo.top - this.moveInfo.originalPositionInfo.top
            };

        this.moveInfo.newPositions =
            (this.moveInfo.selection ?? [])
                .map(item =>
                {
                    return {
                        selectionItem: item,
                        position: {
                            left: item.originalPosition.left + delta.left,
                            top: item.originalPosition.top + delta.top
                        },
                        size: item.associatedControl.size.get()
                    };
                });

        this.moveInfo.valid = true; // Start in a valid state

        this.trigger("moveProcessing", this.moveInfo);

        if (this.moveInfo.valid)
        {
            this.moveInfo.currentPositionInfo = newPositionInfo;
            this.moveInfo.currentPoint = cursorPoint;
        }

        if (!this.moveInfo.hasMoved)
        {
            this.moveInfo.hasMoved =
                this.moveInfo.currentPositionInfo.left !== this._roundValueToGrid(this.moveInfo.originalPositionInfo.left)
                || this.moveInfo.currentPositionInfo.top !== this._roundValueToGrid(this.moveInfo.originalPositionInfo.top);
        }

        this.trigger("moveProcessed", this.moveInfo);

        if (this.moveInfo.valid)
            this._removeStateCss("selection-invalid");
        else
            this._addStateCss("selection-invalid");

        if (this.moveInfo.hasMoved)
        {
            this.moveInfo.newPositions
                .forEach(item =>
                {
                    item.selectionItem.control.position.set(item.position);
                });
        }
    }
    protected _moveEnd(shiftKey: boolean, ctrlKey: boolean)
    {
        if (!this.moveInfo)
            return;

        this.moveInfo.shiftKey = shiftKey;
        this.moveInfo.ctrlKey = ctrlKey;

        if (this.moveInfo.hasMoved || this.moveInfo.hasScrolled)
        {
            if (this.moveInfo.valid)
            {
                const
                    positionInfo =
                        this.moveInfo.newPositions
                            .map(item =>
                            {
                                const
                                    selectionItem = item.selectionItem,
                                    resizeBandOffset = selectionItem.control.$$element.get()?.offset();

                                Assert.isNotNullOrUndefined(resizeBandOffset, "resizeBandOffset");

                                const
                                    selectionContainers =
                                        this._get$$SelectionRoot()
                                            .find(".rad-selection-container")
                                            .toArray()
                                            .map(item =>
                                            {
                                                const
                                                    $$item = $$(item),
                                                    $$itemOffset = $$item.offset();

                                                return {
                                                    $$selectionContainer: $$item,
                                                    offset: { left: $$itemOffset?.left ?? 0, top: $$itemOffset?.top ?? 0 },
                                                    height: $$item.outerHeight()
                                                }
                                            }),
                                    dragDropAreas: IDragDropArea[] = [];

                                // Find Drag / Drop Areas
                                ArrayHelper.orderBy(
                                    selectionContainers,
                                    item => item.offset.top
                                ).forEach((item, index) =>
                                {
                                    const
                                        $$element = item.$$selectionContainer,
                                        offset = item.offset,
                                        height = item.height,
                                        isOffTop = (index === 0 && resizeBandOffset.top < offset.top),
                                        isOffBottom = (index === selectionContainers.length - 1 && resizeBandOffset.top > offset.top + height),
                                        isInBounds = (resizeBandOffset.top >= offset.top && resizeBandOffset.top < offset.top + height);

                                    if (isOffTop || isOffBottom || isInBounds)
                                    {
                                        dragDropAreas.push({
                                            $$element: $$element,
                                            offset: offset,
                                            depth: $$element.parents().length
                                        });
                                    }

                                    if (isOffTop || isOffBottom)
                                        return false;
                                });

                                return {
                                    changedParent: false,
                                    dragDropArea:
                                        ArrayHelper.firstOrDefault(
                                            ArrayHelper.orderByDescending(
                                                dragDropAreas,
                                                item => item.depth
                                            )
                                        ),
                                    position: {
                                        left: item.position.left,
                                        top: item.position.top
                                    },
                                    selectionItem: item.selectionItem,
                                } as IPositionInfo;
                            });

                let
                    topAdjustment = 0;

                ArrayHelper.orderBy(
                    positionInfo,
                    item => { item.position.top; }
                ).forEach(item =>
                {
                    const
                        dragDropArea = item.dragDropArea;

                    Assert.isNotNullOrUndefined(dragDropArea, "dragDropArea");

                    const
                        selectionItem = item.selectionItem,
                        changedParent = !dragDropArea.$$element.is(selectionItem.$$originalParentControl);

                    let
                        position = item.position;

                    if (topAdjustment)
                        item.position.top += topAdjustment;

                    if (changedParent)
                    {
                        const
                            parentOffset = selectionItem.$$originalParentControl.offset();

                        Assert.isNotNullOrUndefined(parentOffset, "parentOffset");

                        position =
                        {
                            left: position.left - (dragDropArea.offset.left - parentOffset.left),
                            top: position.top - (dragDropArea.offset.top - parentOffset.top)
                        };
                    }

                    if (position.top < 0)
                    {
                        topAdjustment -= position.top;
                        position.top = 0;
                    }

                    if (position.left < 0)
                        position.left = 0;

                    const
                        dragDropAreaElementWidth = dragDropArea.$$element.width();

                    Assert.isNotNullOrUndefined(dragDropAreaElementWidth, "dragDropAreaElementWidth");

                    if (position.left > dragDropAreaElementWidth)
                        position.left = dragDropAreaElementWidth;

                    item.changedParent = changedParent;
                    item.position = position;
                });

                this.moveInfo.positionInfo = positionInfo;
                this.moveInfo.cancel = false;
                this.trigger("moveEnding", this.moveInfo);

                if (!this.moveInfo.cancel)
                {
                    ArrayHelper.orderBy(
                        positionInfo,
                        item => { item.position.top; }
                    ).forEach(item =>
                    {
                        Assert.isNotNullOrUndefined(item.dragDropArea, "item.dragDropArea");

                        if (item.changedParent)
                            item.dragDropArea.$$element.radControl().append(item.selectionItem.associatedControl);

                        item.selectionItem.associatedControl.position.set(item.position);
                        item.selectionItem.control.position.set(item.position);
                    });

                    this.trigger("moveEnd", this.moveInfo);
                }
            }
            else
            {
                this.moveInfo.cancel = false;
                this.trigger("moveEndInvalid", this.moveInfo);

                if (!this.moveInfo.cancel)
                {
                    (this.moveInfo.selection ?? [])
                        .forEach(item =>
                        {
                            item.control.position.set(item.control.associatedControl.get()?.position.get() ?? null);
                        });
                }
            }

            if (this.moveInfo.cancel)
            {
                (this.moveInfo.selection ?? [])
                    .forEach(item =>
                    {
                        item.control.hide();
                        item.control.trigger("deselected", this.moveInfo);
                    });
            }
        }
        else
        {
            if (!this.moveInfo.manualStart && (this.moveInfo.shiftKey || this.moveInfo.ctrlKey))
            {
                this.hide();
                this.trigger("deselected", this.moveInfo);
            }
        }

        this.trigger("moveCompleted", this.moveInfo);
        this.moveInfo.$$selectionRoot.off("scroll", { self: this }, this.onMoveScroll);
        delete this.moveInfo;

        this._removeStateCss("moving");
        this._removeStateCss("selection-invalid");

        // Unbind Document Event Handlers
        $$(document).off("keydown", { self: this }, this.onKeyDown);
        $$(document).off("keyup", { self: this }, this.onKeyUp);
        $$(document).off("mousemove touchmove", { self: this }, this.onMoveChanged);
        $$(document).off("mouseup touchend", { self: this }, this.onMoveEnd);
    }
    protected _moveStart(args: IResizeBandMoveStartArgs)
    {
        const
            $$element = this.$$element.get();

        Assert.isNotNullOrUndefined($$element, "$$element");

        const
            positionElement = $$element.position(),
            position = {
                left: positionElement.left ? Math.round(positionElement.left) : undefined,
                top: positionElement.top ? Math.round(positionElement.top) : undefined,
            },
            $$selectionRoot = this._get$$SelectionRoot();

        if (!$$selectionRoot || !$$selectionRoot.length)
            return;

        const
            $$selectionRootOffset = $$selectionRoot.offset();

        this.moveInfo = this._createMoveInfo({
            $$selectionRoot: $$selectionRoot,
            originalSelectionRootOffset: { left: $$selectionRootOffset?.left ?? 0, top: $$selectionRootOffset?.top ?? 0 },
            originalScrollInfo: { left: $$selectionRoot.scrollLeft() ?? 0, top: $$selectionRoot.scrollTop() ?? 0 },
            originalPositionInfo: { left: position.left ?? 0, top: position.top ?? 0 },
            currentPositionInfo: { left: position.left ?? 0, top: position.top ?? 0 },
            startPoint: args.startPoint,
            currentPoint: args.currentPoint || args.startPoint,
            shiftKey: args.shiftKey,
            ctrlKey: args.ctrlKey,
            manualStart: args.manualStart,
            newControl: args.newControl,
            newPositions: [],
            data: args.data,
            valid: false
        });

        this.trigger("moveStarted", this.moveInfo);
        this.moveInfo.$$selectionRoot.on("scroll", { self: this }, this.onMoveScroll);
        this._addStateCss("moving");

        if (args.currentPoint)
            this._moveChanged(args.currentPoint, args.shiftKey, args.ctrlKey);

        // Bind Document Event Handlers
        $$(document).on("keydown", { self: this }, this.onKeyDown);
        $$(document).on("keyup", { self: this }, this.onKeyUp);
        $$(document).on("mousemove touchmove", { self: this }, this.onMoveChanged);
        $$(document).on("mouseup touchend", { self: this }, this.onMoveEnd);
    }
    protected _rectOverlapsItem(items: IRectangle[], rect: IRectangle)
    {
        return (
            items
                .some(item =>
                {
                    return this._rectOverlapsRect(item, rect);
                })
        );
    }
    protected _rectOverlapsRect(rect1: IRectangle, rect2: IRectangle)
    {
        return (
            (
                rect1.x1 < rect2.x1 && rect1.x2 > rect2.x1
                ||
                rect1.x1 >= rect2.x1 && rect1.x1 < rect2.x2
            )
            &&
            (
                rect1.y1 < rect2.y1 && rect1.y2 > rect2.y1
                ||
                rect1.y1 >= rect2.y1 && rect1.y1 < rect2.y2
            )
        );
    }
    protected _removeStateCss(state: string)
    {
        this._get$$SelectionRoot().removeClass(StringHelper.format("rad-state-{0}", state));
    }
    protected _resizeChanged(cursorPoint: Nullable<Point>)
    {
        if (!this.resizeInfo)
            return;

        if (!cursorPoint)
            cursorPoint = this.resizeInfo.currentPoint;

        const
            newScrollInfo = {
                left: this.resizeInfo.$$selectionRoot.scrollLeft() ?? 0,
                top: this.resizeInfo.$$selectionRoot.scrollTop() ?? 0,
            },
            newPositionInfo = {
                left: this.resizeInfo.originalPositionInfo.left,
                top: this.resizeInfo.originalPositionInfo.top
            },
            newSizeInfo = {
                width: this.resizeInfo.originalSizeInfo.width,
                height: this.resizeInfo.originalSizeInfo.height
            },
            resizingHorizontallyFrom =
                this.resizeInfo.$$handle.hasClass("rad-left")
                    ? "left"
                    : this.resizeInfo.$$handle.hasClass("rad-right")
                        ? "right"
                        : "none",
            resizingVerticallyFrom =
                this.resizeInfo.$$handle.hasClass("rad-top")
                    ? "top"
                    : this.resizeInfo.$$handle.hasClass("rad-bottom")
                        ? "bottom"
                        : "none";

        if (resizingHorizontallyFrom === "left")
            newSizeInfo.width = this.resizeInfo.originalSizeInfo.width - cursorPoint.x + this.resizeInfo.startPoint.x - newScrollInfo.left + this.resizeInfo.originalScrollInfo.left;
        else if (resizingHorizontallyFrom === "right")
            newSizeInfo.width = this.resizeInfo.originalSizeInfo.width + cursorPoint.x - this.resizeInfo.startPoint.x + newScrollInfo.left - this.resizeInfo.originalScrollInfo.left;

        if (resizingVerticallyFrom === "top")
            newSizeInfo.height = this.resizeInfo.originalSizeInfo.height - cursorPoint.y + this.resizeInfo.startPoint.y - newScrollInfo.top + this.resizeInfo.originalScrollInfo.top;
        else if (resizingVerticallyFrom === "bottom")
            newSizeInfo.height = this.resizeInfo.originalSizeInfo.height + cursorPoint.y - this.resizeInfo.startPoint.y + newScrollInfo.top - this.resizeInfo.originalScrollInfo.top;

        // Snap to Grid
        newPositionInfo.left = this._roundValueToGrid(newPositionInfo.left);
        newPositionInfo.top = this._roundValueToGrid(newPositionInfo.top);
        newSizeInfo.width = this._roundValueToGrid(newSizeInfo.width);
        newSizeInfo.height = this._roundValueToGrid(newSizeInfo.height);

        // Reposition if resizing from left
        if (resizingHorizontallyFrom === "left")
            newPositionInfo.left = this.resizeInfo.originalPositionInfo.left + this.resizeInfo.originalSizeInfo.width - newSizeInfo.width;

        // Reposition if resizing from top
        if (resizingVerticallyFrom === "top")
            newPositionInfo.top = this.resizeInfo.originalPositionInfo.top + this.resizeInfo.originalSizeInfo.height - newSizeInfo.height;

        const
            minWidth = this.minWidth.get(),
            maxWidth = this.maxWidth.get();

        if (newPositionInfo.left >= 0 && (minWidth === null || newSizeInfo.width >= minWidth) && (maxWidth === null || newSizeInfo.width <= maxWidth))
        {
            // Accept width
            this.resizeInfo.currentPositionInfo.left = newPositionInfo.left;
            this.resizeInfo.currentSizeInfo.width = newSizeInfo.width;
        }

        const
            minHeight = this.minHeight.get(),
            maxHeight = this.maxHeight.get();

        if (newPositionInfo.top >= 0 && (minHeight === null || newSizeInfo.height >= minHeight) && (maxHeight === null || newSizeInfo.height <= maxHeight))
        {
            // Accept height
            this.resizeInfo.currentPositionInfo.top = newPositionInfo.top;
            this.resizeInfo.currentSizeInfo.height = newSizeInfo.height;
        }

        this.resizeInfo.valid = true;

        this.trigger("resizeChanged", this);

        if (this.resizeInfo.valid)
        {
            this._removeStateCss("selection-invalid");
            this.resizeInfo.currentPoint = cursorPoint;

            const
                associatedControl = this.associatedControl.get();

            Assert.isNotNullOrUndefined(associatedControl, "associatedControl");

            associatedControl.position.set(this.resizeInfo.currentPositionInfo);
            associatedControl.size.set(this.resizeInfo.currentSizeInfo);
        }
        else
            this._addStateCss("selection-invalid");

        this.position.set(this.resizeInfo.currentPositionInfo);
        this.size.set(this.resizeInfo.currentSizeInfo);
    }
    protected _resizeEnd()
    {
        if (!this.resizeInfo)
            return;

        const
            associatedControl = this.associatedControl.get();

        Assert.isNotNullOrUndefined(associatedControl, "associatedControl");

        if (this.resizeInfo.valid)
        {
            this.resizeInfo.cancel = false;
            this.trigger("resizeEnding", this.resizeInfo);

            if (!this.resizeInfo.cancel)
            {
                associatedControl.position.set(this.resizeInfo.currentPositionInfo);
                associatedControl.size.set(this.resizeInfo.currentSizeInfo);

                this.trigger("resizeEnd", this.resizeInfo);
            }
        }
        else
        {
            associatedControl.position.set(this.resizeInfo.originalPositionInfo);
            associatedControl.size.set(this.resizeInfo.originalSizeInfo);

            this.position.set(this.resizeInfo.originalPositionInfo);
            this.size.set(this.resizeInfo.originalSizeInfo);
        }

        this.resizeInfo.$$selectionRoot.off("scroll", { self: this }, this.onResizeScroll);
        delete this.resizeInfo;

        this._removeStateCss("resizing");
        this._removeStateCss("selection-invalid");

        // Unbind Document Event Handlers
        $$(document).off("mousemove touchmove", { self: this }, this.onResizeChanged);
        $$(document).off("mouseup touchend", { self: this }, this.onResizeEnd);
    }
    protected _resizeModeChanged(change: PropertyChange<ResizeBandResizeModeEnum>)
    {
        if (change.originalValue)
            this.removeCssClass(StringHelper.format("rad-mode-{0}", StringHelper.toDashed(change.originalValue)));

        if (change.currentValue)
            this.addCssClass(StringHelper.format("rad-mode-{0}", StringHelper.toDashed(change.currentValue)));
    }
    protected _resizeStart($$handle: JNative<HTMLElement>, startPoint: Point, currentPoint: Point)
    {
        const
            $$element = this.$$element.get();

        Assert.isNotNullOrUndefined($$element, "$$element");

        const
            positionElement = $$element.position(),
            position = {
                left: positionElement.left ? Math.round(positionElement.left) : 0,
                top: positionElement.top ? Math.round(positionElement.top) : 0,
            },
            $$selectionRoot = this._get$$SelectionRoot();

        if (!$$selectionRoot || !$$selectionRoot.length)
            return;

        const
            $$selectionRootScrollLeft = $$selectionRoot.scrollLeft(),
            $$selectionRootScrollTop = $$selectionRoot.scrollTop(),
            $$elementWidth = $$element.width(),
            $$elementHeight = $$element.height();

        Assert.isNotNullOrUndefined($$selectionRootScrollLeft, "$$selectionRootScrollLeft");
        Assert.isNotNullOrUndefined($$selectionRootScrollTop, "$$selectionRootScrollTop");
        Assert.isNotNullOrUndefined($$elementWidth, "$$elementWidth");
        Assert.isNotNullOrUndefined($$elementHeight, "$$elementHeight");

        this.resizeInfo = {
            $$selectionRoot: $$selectionRoot,
            $$handle: $$handle,
            originalScrollInfo: { left: $$selectionRootScrollLeft, top: $$selectionRootScrollTop },
            originalPositionInfo: { left: position.left, top: position.top },
            originalSizeInfo: { width: $$elementWidth, height: $$elementHeight },
            currentPositionInfo: { left: position.left, top: position.top },
            currentSizeInfo: { width: $$elementWidth, height: $$elementHeight },
            startPoint: startPoint,
            currentPoint: currentPoint,
            valid: false
        };

        this.resizeInfo.$$selectionRoot.on("scroll", { self: this }, this.onResizeScroll);
        this._addStateCss("resizing");

        // Bind Document Event Handlers
        $$(document).on("mousemove touchmove", { self: this }, this.onResizeChanged);
        $$(document).on("mouseup touchend", { self: this }, this.onResizeEnd);
    }
    protected _roundValueToGrid(value: number)
    {
        const
            gridSpacing = this.gridSpacing.get() ?? 1;

        return Math.round(value / gridSpacing) * gridSpacing;
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: ResizeBandPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.associatedControl = new Property<IResizableControl>(this, "System.Object, System.Private.CoreLib", propertyValues.associatedControl, this._associatedControlChanged);
        this.canMove = new PropertyBoolean(this, propertyValues.canMove, this._canMoveChanged);
        this.canResize = new PropertyBoolean(this, propertyValues.canResize, this._canResizeChanged);
        this.gridSpacing = new PropertyInt32(this, propertyValues.gridSpacing, this._gridSpacingChanged);
        this.resizeMode = new Property<ResizeBandResizeModeEnum>(this, "System.String, System.Private.CoreLib", propertyValues.resizeMode, this._resizeModeChanged);
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        this.on("mousedown touchstart", { self: this }, this.onInputStart);
        this.on("mousedown touchstart", ">.rad-band2", { self: this }, this.onMoveStart);
        this.on("mousedown touchstart", ">.rad-handle", { self: this }, this.onResizeStart);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        Assert.isNotNullOrUndefined($$element, "$$element");

        $$element.append(JNativeStatic.create([
            { nodeName: "div", attributes: { "class": "rad-band1" } },
            { nodeName: "div", attributes: { "class": "rad-band2" } },
            { nodeName: "div", attributes: { "class": "rad-handle rad-top rad-left" } },
            { nodeName: "div", attributes: { "class": "rad-handle rad-top rad-center" } },
            { nodeName: "div", attributes: { "class": "rad-handle rad-top rad-right" } },
            { nodeName: "div", attributes: { "class": "rad-handle rad-middle rad-left" } },
            { nodeName: "div", attributes: { "class": "rad-handle rad-middle rad-right" } },
            { nodeName: "div", attributes: { "class": "rad-handle rad-bottom rad-left" } },
            { nodeName: "div", attributes: { "class": "rad-handle rad-bottom rad-center" } },
            { nodeName: "div", attributes: { "class": "rad-handle rad-bottom rad-right" } }
        ]));

        return $$element;
    }
    public override show()
    {
        const
            associatedControl = this.associatedControl.get();

        if (!associatedControl)
            return;

        this.position.set(associatedControl.position.get());
        this.size.set(associatedControl.size.get());

        super.show();
    }

    // #endregion

    // #region Event Handlers

    protected onInputStart(evt: IJNativeEvent)
    {
        evt.stopPropagation();
    }
    protected onKeyDown(evt: IJNativeEvent<KeyboardEvent>)
    {
        const
            self = evt.data.self as this;

        if (evt.originalEvent.key === "Shift" || evt.originalEvent.key === "Control")
            self._moveChanged(null, evt.originalEvent.shiftKey, evt.originalEvent.ctrlKey);
    }
    protected onKeyUp(evt: IJNativeEvent<KeyboardEvent>)
    {
        const
            self = evt.data.self as this;

        if (evt.originalEvent.key === "Shift" || evt.originalEvent.key === "Control")
            self._moveChanged(null, evt.originalEvent.shiftKey, evt.originalEvent.ctrlKey);
    }
    protected onMoveChanged(evt: IJNativeEvent<MouseEvent | TouchEvent>)
    {
        const
            self = evt.data.self as this;

        if (!self.moveInfo)
            return;

        evt.preventDefault();

        self._moveChanged(Point.fromEvent(evt), evt.originalEvent.shiftKey, evt.originalEvent.ctrlKey);
    }
    protected onMoveEnd(evt: IJNativeEvent<MouseEvent | TouchEvent>)
    {
        const
            self = evt.data.self as this;

        if (!self.moveInfo)
            return;

        evt.preventDefault();

        self._moveEnd(evt.originalEvent.shiftKey, evt.originalEvent.ctrlKey);
    }
    protected onMoveScroll(evt: IJNativeEvent<MouseEvent | TouchEvent>)
    {
        const
            self = evt.data.self as this;

        if (!self.moveInfo)
            return;

        self.moveInfo.hasScrolled = true;

        self._moveChanged(null, evt.originalEvent.shiftKey, evt.originalEvent.ctrlKey);
    }
    protected onMoveStart(evt: IJNativeEvent<MouseEvent | TouchEvent>)
    {
        const
            self = evt.data.self as this;

        if (self.moveInfo)
            return;

        evt.preventDefault();
        evt.stopPropagation();

        const
            inputStartData = { cancel: false };

        self.trigger("inputStart", inputStartData);

        if (inputStartData.cancel)
            return;

        self._moveStart({
            startPoint: Point.fromEvent(evt),
            currentPoint: Point.fromEvent(evt),
            shiftKey: evt.originalEvent.shiftKey,
            ctrlKey: evt.originalEvent.ctrlKey,
            newControl: false,
            manualStart: false,
            data: null
        });
    }
    protected onResizeChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        if (!self.resizeInfo)
            return;

        evt.preventDefault();

        self._resizeChanged(Point.fromEvent(evt));
    }
    protected onResizeEnd(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        if (!self.resizeInfo)
            return;

        evt.preventDefault();

        self._resizeEnd();
    }
    protected onResizeScroll(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        if (!self.resizeInfo)
            return;

        self.resizeInfo.hasScrolled = true;

        self._resizeChanged(null);
    }
    protected onResizeStart(evt: IJNativeEvent)
    {
        if (!(evt.target instanceof HTMLElement))
            return;

        const
            self = evt.data.self as this;

        if (self.resizeInfo)
            return;

        evt.preventDefault();
        evt.stopPropagation();

        const
            inputStartData = { cancel: false };

        self.trigger("inputStart", inputStartData);

        if (inputStartData.cancel)
            return;

        self._resizeStart($$(evt.target), Point.fromEvent(evt), Point.fromEvent(evt));
    }

    // #endregion

    // #endregion
}